import { TestDefinition, QuestionType } from '../../types';

// Scale 1-5 for WHODAS 2.0
const whodasScale = [
  { label: 'None', value: 1 },
  { label: 'Mild', value: 2 },
  { label: 'Moderate', value: 3 },
  { label: 'Severe', value: 4 },
  { label: 'Extreme/Cannot do', value: 5 }
];

// Scale 0-10 for Sheehan
const sheehanScale = [
  { label: '0 - Not at all', value: 0 },
  { label: '1', value: 1 },
  { label: '2', value: 2 },
  { label: '3', value: 3 },
  { label: '4', value: 4 },
  { label: '5 - Moderate', value: 5 },
  { label: '6', value: 6 },
  { label: '7', value: 7 },
  { label: '8', value: 8 },
  { label: '9', value: 9 },
  { label: '10 - Extreme', value: 10 }
];

export const TESTS_DISCAPACIDAD: TestDefinition[] = [
  {
    id: 'whodas-2.0',
    code: 'WHODAS 2.0',
    name: 'WHO Disability Assessment Schedule 2.0 (12-item)',
    category: 'Disability',
    description: 'WHO Disability Assessment Schedule short version. 12 items assessing difficulties in daily activities in the last 30 days. Includes 6 domains: cognition, mobility, self-care, relationships, daily activities and participation.',
    questions: [
      // COGNITION (2 items)
      {
        id: 'w1',
        text: 'D1.1 Concentrating on doing something for ten minutes',
        type: QuestionType.LIKERT,
        options: whodasScale,
        subscale: 'Cognition'
      },
      {
        id: 'w2',
        text: 'D1.2 Remembering to do important things',
        type: QuestionType.LIKERT,
        options: whodasScale,
        subscale: 'Cognition'
      },

      // MOBILITY (2 items)
      {
        id: 'w3',
        text: 'D2.1 Standing for long periods such as 30 minutes',
        type: QuestionType.LIKERT,
        options: whodasScale,
        subscale: 'Mobility'
      },
      {
        id: 'w4',
        text: 'D2.2 Moving around inside your home',
        type: QuestionType.LIKERT,
        options: whodasScale,
        subscale: 'Mobility'
      },

      // SELF-CARE (2 items)
      {
        id: 'w5',
        text: 'D3.1 Washing your whole body',
        type: QuestionType.LIKERT,
        options: whodasScale,
        subscale: 'Self-care'
      },
      {
        id: 'w6',
        text: 'D3.2 Getting dressed',
        type: QuestionType.LIKERT,
        options: whodasScale,
        subscale: 'Self-care'
      },

      // RELATIONSHIPS (2 items)
      {
        id: 'w7',
        text: 'D4.1 Dealing with people you do not know',
        type: QuestionType.LIKERT,
        options: whodasScale,
        subscale: 'Relationships'
      },
      {
        id: 'w8',
        text: 'D4.2 Maintaining a friendship',
        type: QuestionType.LIKERT,
        options: whodasScale,
        subscale: 'Relationships'
      },

      // DAILY ACTIVITIES (2 items)
      {
        id: 'w9',
        text: 'D5.1 Taking care of your household responsibilities',
        type: QuestionType.LIKERT,
        options: whodasScale,
        subscale: 'Daily Activities'
      },
      {
        id: 'w10',
        text: 'D5.2 Doing most important household tasks well',
        type: QuestionType.LIKERT,
        options: whodasScale,
        subscale: 'Daily Activities'
      },

      // PARTICIPATION (2 items)
      {
        id: 'w11',
        text: 'D6.1 Day-to-day work/school activities',
        type: QuestionType.LIKERT,
        options: whodasScale,
        subscale: 'Participation'
      },
      {
        id: 'w12',
        text: 'D6.2 Being emotionally affected by your health problems',
        type: QuestionType.LIKERT,
        options: whodasScale,
        subscale: 'Participation'
      }
    ],
    subscales: [
      {
        name: 'Cognition',
        questionIds: ['w1', 'w2'],
        description: 'Understanding and communicating'
      },
      {
        name: 'Mobility',
        questionIds: ['w3', 'w4'],
        description: 'Moving and getting around'
      },
      {
        name: 'Self-care',
        questionIds: ['w5', 'w6'],
        description: 'Hygiene, dressing, eating'
      },
      {
        name: 'Relationships',
        questionIds: ['w7', 'w8'],
        description: 'Interacting with other people'
      },
      {
        name: 'Daily Activities',
        questionIds: ['w9', 'w10'],
        description: 'Household responsibilities and work'
      },
      {
        name: 'Participation',
        questionIds: ['w11', 'w12'],
        description: 'Community and social activities'
      }
    ],
    interpretations: [
      { min: 12, max: 18, label: 'No Disability', color: 'bg-green-100 text-green-800' },
      { min: 19, max: 28, label: 'Mild Disability', color: 'bg-yellow-100 text-yellow-800' },
      { min: 29, max: 38, label: 'Moderate Disability', color: 'bg-orange-100 text-orange-800' },
      { min: 39, max: 48, label: 'Severe Disability', color: 'bg-red-100 text-red-800' },
      { min: 49, max: 60, label: 'Extreme Disability', color: 'bg-red-200 text-red-900' }
    ]
  },
  {
    id: 'sheehan',
    code: 'SDS',
    name: 'Sheehan Disability Scale',
    category: 'Disability',
    description: 'A brief 3-item scale to assess functional impairment in work, social life, and family life. Developed by Sheehan (1983). Visual analogue scale 0-10 for each domain.',
    questions: [
      {
        id: 's1',
        text: '1. Work/School: The symptoms have disrupted your work/school work',
        type: QuestionType.LIKERT,
        options: sheehanScale
      },
      {
        id: 's2',
        text: '2. Social Life: The symptoms have disrupted your social life',
        type: QuestionType.LIKERT,
        options: sheehanScale
      },
      {
        id: 's3',
        text: '3. Family Life: The symptoms have disrupted your family life/home responsibilities',
        type: QuestionType.LIKERT,
        options: sheehanScale
      }
    ],
    interpretations: [
      { min: 0, max: 5, label: 'No Functional Impairment', color: 'bg-green-100 text-green-800' },
      { min: 6, max: 12, label: 'Mild Impairment', color: 'bg-yellow-100 text-yellow-800' },
      { min: 13, max: 20, label: 'Moderate Impairment', color: 'bg-orange-100 text-orange-800' },
      { min: 21, max: 30, label: 'Severe Impairment', color: 'bg-red-200 text-red-900' }
    ]
  }
];
